/**
 * Membership API — Central-backed workspace member management.
 *
 * Uses shared path builders from @levelup/central-auth.
 * All requests go browser-direct to Central with Bearer token auth.
 * No cookies. No proxy.
 */

import {
  buildMembersPath,
  buildMemberPath,
  buildInvitesPath,
  buildInvitePath,
  buildInviteResendPath,
} from '@levelup/central-auth';
import type {
  WorkspaceMember,
  PendingInvite,
  CentralRole,
  InviteRoleOption,
} from '@levelup/central-auth';
import {
  APP_ROLE_PROFILES,
  type AppRoleKey,
} from '../../../shared/src/appRoles';

const CENTRAL_URL = import.meta.env.VITE_CENTRAL_URL || '';

function centralUrl(path: string): string {
  return `${CENTRAL_URL}${path}`;
}

function authHeaders(token: string, extra: Record<string, string> = {}): Record<string, string> {
  return {
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
    ...extra,
  };
}

async function readJson(res: Response): Promise<unknown> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    const message = typeof body.message === 'string' ? body.message : `Request failed: ${res.status}`;
    throw new Error(message);
  }
  return res.json();
}

/**
 * Pull an array out of one of the common Central envelope shapes:
 *   - direct array:                   `[...]`
 *   - data wrapper:                   `{ data: [...] }`
 *   - keyed wrapper (members/invites):`{ members: [...] }` / `{ invites: [...] }`
 *   - nested wrapper:                 `{ data: { members: [...] } }`
 */
function extractArray<T>(body: unknown, key: string): T[] {
  if (Array.isArray(body)) return body as T[];
  if (body && typeof body === 'object') {
    const obj = body as Record<string, unknown>;
    if (Array.isArray(obj[key])) return obj[key] as T[];
    if (Array.isArray(obj.data)) return obj.data as T[];
    if (obj.data && typeof obj.data === 'object') {
      const data = obj.data as Record<string, unknown>;
      if (Array.isArray(data[key])) return data[key] as T[];
    }
  }
  return [];
}

export async function listMembers(token: string, workspaceId: string): Promise<WorkspaceMember[]> {
  const res = await fetch(centralUrl(buildMembersPath(workspaceId)), {
    headers: authHeaders(token),
  });
  return extractArray<WorkspaceMember>(await readJson(res), 'members');
}

export async function inviteMember(
  token: string,
  workspaceId: string,
  params: { email: string; role: CentralRole; templateId?: string; message?: string },
): Promise<void> {
  const res = await fetch(centralUrl(buildMembersPath(workspaceId)), {
    method: 'POST',
    headers: authHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as Record<string, string>).message || `Invite failed: ${res.status}`);
  }
}

export async function updateMemberRole(
  token: string,
  workspaceId: string,
  userId: string,
  newRole: CentralRole,
): Promise<void> {
  const res = await fetch(centralUrl(buildMemberPath(workspaceId, userId)), {
    method: 'PATCH',
    headers: authHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify({ role: newRole }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as Record<string, string>).message || `Role update failed: ${res.status}`);
  }
}

export async function removeMember(token: string, workspaceId: string, userId: string): Promise<void> {
  const res = await fetch(centralUrl(buildMemberPath(workspaceId, userId)), {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as Record<string, string>).message || `Remove failed: ${res.status}`);
  }
}

export async function listPendingInvites(token: string, workspaceId: string): Promise<PendingInvite[]> {
  const res = await fetch(centralUrl(buildInvitesPath(workspaceId)), {
    headers: authHeaders(token),
  });
  return extractArray<PendingInvite>(await readJson(res), 'invites');
}

export async function resendInvite(token: string, workspaceId: string, inviteId: string): Promise<void> {
  const res = await fetch(centralUrl(buildInviteResendPath(workspaceId, inviteId)), {
    method: 'POST',
    headers: authHeaders(token),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as Record<string, string>).message || `Resend failed: ${res.status}`);
  }
}

export async function cancelInvite(token: string, workspaceId: string, inviteId: string): Promise<void> {
  const res = await fetch(centralUrl(buildInvitePath(workspaceId, inviteId)), {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as Record<string, string>).message || `Cancel failed: ${res.status}`);
  }
}

// ============================================================
// Invite Role Options — Central templates + BeHeard fallback
// ============================================================

const APP_ID = 'beheard';

/** Core roles that appear as top-level invite options. */
const ALLOWED_CORE_ROLES: readonly CentralRole[] = ['owner', 'admin', 'viewer'];

/** BeHeard member profile keys that map to Central capability templates. */
const BEHEARD_MEMBER_TEMPLATES: readonly AppRoleKey[] = [
  'member_standard',
  'member_premium',
  'member_recruiter',
  'member_trainer',
  'member_org_admin',
];

function isValidBeheardInviteOption(opt: InviteRoleOption): boolean {
  if (opt.group === 'core') {
    return ALLOWED_CORE_ROLES.includes(opt.coreRole);
  }
  if (opt.group === 'template') {
    return (
      opt.coreRole === 'member' &&
      typeof opt.templateId === 'string' &&
      (BEHEARD_MEMBER_TEMPLATES as readonly string[]).includes(opt.templateId)
    );
  }
  return false;
}

/**
 * Build local InviteRoleOption[] from BeHeard's profile definitions.
 *
 * Used as a fallback when Central's invite-options endpoint hasn't been
 * configured with BeHeard capability templates yet. The shared
 * InviteMemberModal renders the same rich dropdown UI either way.
 */
function buildLocalInviteOptions(): InviteRoleOption[] {
  const options: InviteRoleOption[] = [];

  // Core roles (owner, admin, viewer)
  options.push(
    { id: 'core-owner', coreRole: 'owner', templateId: null, displayLabel: 'Owner', description: 'Full workspace + BeHeard access.', group: 'core' },
    { id: 'core-admin', coreRole: 'admin', templateId: null, displayLabel: 'Admin', description: 'Full BeHeard admin access (via beheard:admin).', group: 'core' },
    { id: 'core-viewer', coreRole: 'viewer', templateId: null, displayLabel: 'Viewer', description: 'Read-only access (Central enforced).', group: 'core' },
  );

  // Member template roles (from APP_ROLE_PROFILES)
  for (const key of BEHEARD_MEMBER_TEMPLATES) {
    const profile = APP_ROLE_PROFILES.find((p) => p.key === key);
    if (!profile) continue;
    options.push({
      id: `template-${key}`,
      coreRole: 'member',
      templateId: key,
      displayLabel: `Member — ${profile.displayName}`,
      description: profile.description,
      group: 'template',
    });
  }

  return options;
}

/**
 * Fetch invite-role options from Central, filtered for BeHeard.
 *
 * GET /api/workspaces/:workspaceId/invite-options?appId=beheard
 *
 * Falls back to locally-built options (from APP_ROLE_PROFILES) if Central
 * hasn't been configured with BeHeard capability templates yet.
 */
/**
 * Module-level constant — the BeHeard invite-role options.
 * Available synchronously on import. No function call needed.
 */
export const BEHEARD_INVITE_ROLE_OPTIONS: InviteRoleOption[] = buildLocalInviteOptions();

export async function fetchInviteOptions(
  token: string,
  workspaceId: string,
): Promise<InviteRoleOption[]> {
  try {
    const url = `${centralUrl(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/invite-options`,
    )}?appId=${encodeURIComponent(APP_ID)}`;
    const res = await fetch(url, { headers: authHeaders(token) });
    if (!res.ok) {
      // Central invite-options not available — use local fallback
      return buildLocalInviteOptions();
    }
    const body = await readJson(res);
    const raw = extractArray<InviteRoleOption>(body, 'options');
    const filtered = raw.filter(isValidBeheardInviteOption);
    // Use Central's list if it has BeHeard templates; else local fallback
    return filtered.length > 0 ? filtered : buildLocalInviteOptions();
  } catch {
    return buildLocalInviteOptions();
  }
}

export type { InviteRoleOption };
